import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  CheckCircle2,
  AlertCircle,
  Edit3,
  ArrowRight,
  FileCheck,
} from "lucide-react";
import useConfirm from "../../hooks/useConfirm.js";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog.js";
import "./style.css";

// Propriedades esperadas em cada campo:
// data = [
//   { id: 'dados-pessoais', section: 'Dados Pessoais', fields: [{ label: 'Nome', value: 'Giovana Costa', complete: true }] }
// ]

// Função utilitária que define se um campo está realmente preenchido.
// Considera preenchido apenas quando tem valor E a flag `complete` é true
// (mantém consistência entre a barra de progresso e os ícones de status).
const isFieldFilled = (field) => {
  if (!field) return false;
  if (!field.value) return false;
  if (typeof field.value === "string" && field.value.trim() === "")
    return false;
  return field.complete === true;
};

export default function FormReview({ data, onEdit, onSubmit, confirmMessage }) {
  // Validação defensiva: se `data` vier undefined/null/vazio, evita quebra.
  const safeData = Array.isArray(data) ? data : [];
  // Hook para diálogo elegante (substitui window.confirm)
  const { confirm, confirmDialog: confirmDialogReview } = useConfirm();

  // Calcula o progresso total de preenchimento (memoizado para evitar recálculos)
  const { progressPercentage, isFullyComplete, incompleteSectionsCount } =
    useMemo(() => {
      const allFields = safeData.flatMap((section) => section.fields || []);
      const total = allFields.length;

      if (total === 0) {
        return {
          progressPercentage: 0,
          isFullyComplete: false,
          incompleteSectionsCount: 0,
        };
      }

      const completed = allFields.filter(isFieldFilled).length;
      const percentage = Math.round((completed / total) * 100);

      // Conta quantas seções ainda têm pelo menos um campo pendente
      const incomplete = safeData.filter((section) => {
        const fields = section.fields || [];
        return fields.some((f) => !isFieldFilled(f));
      }).length;

      return {
        progressPercentage: percentage,
        isFullyComplete: percentage === 100,
        incompleteSectionsCount: incomplete,
      };
    }, [safeData]);

  const handleConfirm = async () => {
    if (!isFullyComplete) return;
    if (confirmMessage) {
      const ok = await confirm({
        title: "Confirmar envio",
        message: confirmMessage,
        confirmText: "Sim, enviar",
        cancelText: "Cancelar",
        variant: "default",
      });
      if (!ok) return;
    }
    onSubmit?.();
  };

  return (
    <div className="fr-container">
      {/* Cabeçalho com Barra de Progresso */}
      <div className="fr-header">
        <div className="fr-header-top">
          <div className="fr-header-info">
            <div className="fr-icon-box">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="fr-title">Conferência de Dados</h2>
              <p className="fr-subtitle">
                Revise as informações antes de prosseguir
              </p>
            </div>
          </div>
          <span
            className={`fr-badge ${
              isFullyComplete ? "fr-badge-complete" : "fr-badge-incomplete"
            }`}
          >
            {progressPercentage}% Preenchido
          </span>
        </div>

        {/* Track da Barra de Progresso */}
        <div className="fr-progress-track">
          <div
            className="fr-progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Lista de Seções e Campos */}
      <div className="fr-sections">
        {safeData.map((section) => {
          const sectionFields = section.fields || [];
          const sectionFilled = sectionFields.every(isFieldFilled);

          return (
            <div key={section.id} className="fr-section">
              {/* Título da Seção + Botão de Editar */}
              <div className="fr-section-header">
                <div className="fr-section-title-wrapper">
                  <h3 className="fr-section-title">{section.section}</h3>
                  {sectionFilled ? (
                    <CheckCircle2
                      className="fr-section-icon fr-section-icon-complete"
                      aria-label="Seção completa"
                    />
                  ) : (
                    <AlertCircle
                      className="fr-section-icon fr-section-icon-incomplete"
                      aria-label="Seção com pendências"
                    />
                  )}
                </div>
                {onEdit && (
                  <button
                    onClick={() => onEdit(section.id)}
                    className="fr-edit-btn"
                  >
                    <Edit3 />
                    Editar
                  </button>
                )}
              </div>

              {/* Grid de Campos */}
              <div className="fr-fields-grid">
                {sectionFields.map((field, idx) => {
                  const isFullWidth = field.label === "Endereço" || field.label === "Endereço Residencial VT" || field.label === "Meios de Transporte";
                  const filled = isFieldFilled(field);
                  return (
                    <div
                      key={`${section.id}-${idx}`}
                      className={`fr-field ${isFullWidth ? "fr-field-full-width" : ""}`}
                    >
                      <span className="fr-field-label">{field.label}</span>
                      <div className="fr-field-value-wrapper">
                        <span
                          title={
                            filled
                              ? String(field.value)
                              : "Campo não preenchido"
                          }
                          className={`fr-field-value ${
                            filled ? "" : "fr-field-value-empty"
                          }`}
                        >
                          {filled ? field.value : "Não preenchido"}
                        </span>

                        {/* Ícone de Status do Campo */}
                        {filled ? (
                          <CheckCircle2
                            className="fr-field-icon fr-field-icon-complete"
                            aria-label="Campo preenchido"
                          />
                        ) : (
                          <AlertCircle
                            className="fr-field-icon fr-field-icon-incomplete"
                            aria-label="Campo pendente"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Aviso de seções pendentes */}
        {!isFullyComplete && incompleteSectionsCount > 0 && (
          <p className="fr-pending-warning">
            {incompleteSectionsCount === 1
              ? "1 seção ainda possui campos pendentes"
              : `${incompleteSectionsCount} seções ainda possuem campos pendentes`}
          </p>
        )}
      </div>

      {/* Ações / Botão Final */}
      <div className="fr-footer">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isFullyComplete}
          title={
            isFullyComplete
              ? "Enviar formulário"
              : "Preencha todos os campos antes de enviar"
          }
          className={`fr-submit-btn ${
            isFullyComplete ? "fr-submit-btn-enabled" : "fr-submit-btn-disabled"
          }`}
        >
          Confirmar e Enviar
          <ArrowRight />
        </button>
      </div>
      {/* Diálogo de confirmação elegante (substitui window.confirm) */}
      {confirmDialogReview}
    </div>
  );
}

// Validação de tipos das props
FormReview.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      section: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          complete: PropTypes.bool,
        }),
      ).isRequired,
    }),
  ),
  onEdit: PropTypes.func,
  onSubmit: PropTypes.func,
  confirmMessage: PropTypes.string,
};

FormReview.defaultProps = {
  data: [],
  onEdit: undefined,
  onSubmit: () => {},
  confirmMessage: undefined,
};
